/**
 * useFieldHelp Hook (Feature #55)
 *
 * React hook for fetching field-level contextual help from the AI backend.
 * Provides tooltip data with caching at the API level.
 *
 * @module useFieldHelp
 */

'use client';

import { useCallback, useState } from 'react';

import { apiClient } from '@/lib/api-client';

// ============================================================================
// TYPES
// ============================================================================

export interface FieldHelpData {
  fieldId: string;
  tooltip: string;
  detailed?: string;
  enumValues?: string[];
  relatedTourId?: string;
  sources: string[];
  fromCache: boolean;
}

export interface UseFieldHelpReturn {
  helpData: FieldHelpData | null;
  isLoading: boolean;
  error: string | null;
  fetchHelp: (page: string, fieldId: string, lang?: 'en' | 'tr') => Promise<void>;
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook for fetching field-level AI help.
 *
 * @example
 * ```tsx
 * const { helpData, isLoading, fetchHelp } = useFieldHelp();
 * // On hover:
 * await fetchHelp('/orders', 'status', 'en');
 * ```
 */
export function useFieldHelp(): UseFieldHelpReturn {
  const [helpData, setHelpData] = useState<FieldHelpData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHelp = useCallback(async (page: string, fieldId: string, lang: 'en' | 'tr' = 'en') => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<FieldHelpData>('/ai-chatbot/help/field', {
        params: { page, field: fieldId, lang },
      });
      setHelpData(response.data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch help';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { helpData, isLoading, error, fetchHelp };
}
